import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ChevronDown, QrCode } from "lucide-react";

// Photorealistic-feeling iPhone silhouette with layered in-app UI simulated in HTML/CSS.
export default function PhoneMockup() {
  return (
    <div className="relative" data-testid="phone-mockup">
      {/* soft floor shadow */}
      <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-64 h-6 rounded-full bg-forest/25 blur-2xl" />
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 6, ease: "easeInOut", repeat: Infinity }}
        className="phone-shell relative w-[300px] sm:w-[330px] md:w-[360px] aspect-[9/19] p-[10px]"
      >
        {/* Notch */}
        <div className="absolute left-1/2 -translate-x-1/2 top-3 w-24 h-6 bg-black/90 rounded-full z-20" />
        {/* Screen */}
        <div className="relative w-full h-full rounded-[34px] overflow-hidden bg-canvas">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[11px] font-medium text-ink/70">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span>5G</span>
              <span className="ml-1">●●●●</span>
            </span>
          </div>

          {/* Header */}
          <div className="px-5 pt-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-ink/50">Good morning</div>
              <div className="text-lg font-display text-forest">Phil-Kholynes</div>
            </div>
            <div
              className="w-9 h-9 rounded-full ring-2 ring-lime/40 shadow-[0_4px_14px_-4px_rgba(45,13,94,0.5)] grid place-items-center bg-gradient-to-br from-forest via-forest to-[#2D0D5E] text-lime text-xs font-semibold font-mono tracking-wider"
              aria-label="Phil-Kholynes Chidi"
              title="Phil-Kholynes Chidi"
              data-testid="phone-mockup-avatar"
            >
              PK
            </div>
          </div>

          {/* Balance card */}
          <div className="mx-5 mt-4 rounded-2xl bg-forest text-canvas p-4 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-lime/20 blur-2xl" />
            <div className="text-[10px] tracking-[0.22em] uppercase text-canvas/60">Balance</div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-display text-3xl">$2,485</span>
              <span className="text-canvas/60 text-sm">.20</span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <IconPill label="Send" primary />
              <IconPill label="Request" />
              <IconPill label="Scan" icon={<QrCode className="w-3 h-3" />} />
            </div>
          </div>

          {/* Convert row */}
          <div className="mx-5 mt-4 rounded-2xl border border-forest/10 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink/50">You send</div>
                <div className="font-display text-xl text-ink">$500.00</div>
              </div>
              <span className="text-[10px] px-2 py-1 rounded-full bg-forest/10 text-forest font-semibold">USD</span>
            </div>
            <div className="my-3 flex items-center gap-2 text-ink/40">
              <div className="flex-1 h-px bg-forest/10" />
              <div className="w-7 h-7 rounded-full border border-forest/15 grid place-items-center">
                <ChevronDown className="w-3 h-3" />
              </div>
              <div className="flex-1 h-px bg-forest/10" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ink/50">They receive</div>
                <div className="font-display text-xl text-forest">₦ 792,500.00</div>
              </div>
              <span className="text-[10px] px-2 py-1 rounded-full bg-lime text-ink font-semibold">NGN</span>
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] text-ink/60">
              <span>Rate 1 USD = 1,585 NGN</span>
              <span className="inline-flex items-center gap-1">
                <span className="live-dot" /> mid-market
              </span>
            </div>
          </div>

          {/* Recipient row */}
          <div className="mx-5 mt-4 rounded-2xl border border-forest/10 p-3 flex items-center gap-3 bg-white">
            <div className="w-9 h-9 rounded-full bg-lime/50 grid place-items-center font-semibold text-ink">A</div>
            <div className="flex-1">
              <div className="text-sm font-medium text-ink">Adaeze O.</div>
              <div className="text-[11px] text-ink/50">GTB · 022•••4471</div>
            </div>
            <ArrowRight className="w-4 h-4 text-forest" />
          </div>

          {/* Send button */}
          <div className="absolute bottom-5 left-5 right-5">
            <div className="rounded-full bg-forest text-canvas h-11 flex items-center justify-between px-5">
              <span className="text-sm font-medium">Send now · Arrives in ~2 min</span>
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function IconPill({ label, primary, icon }) {
  return (
    <div
      className={`h-7 px-3 rounded-full text-[11px] font-medium flex items-center gap-1.5 ${
        primary ? "bg-lime text-ink" : "bg-canvas/10 text-canvas border border-canvas/15"
      }`}
    >
      {icon}
      {label}
    </div>
  );
}
